import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Download, Search, CheckCircle2, Circle, LogOut, KeyRound, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useAdminSession } from "@/hooks/useAdminSession";
import { AdminLoginForm } from "@/components/shared/AdminLoginForm";
import { AdminChangePasswordModal } from "@/components/shared/AdminChangePasswordModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSV_COLUMNS = [
  ["name", "Name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["role", "Role"],
  ["segment", "Segment"],
  ["location", "Location"],
  ["state", "State"],
  ["referral_code", "Referral Code"],
  ["synced_to_app", "Synced To App"],
  ["created_at", "Created At"],
];

const toCSV = (leads) => {
  const rows = leads.map((l) => CSV_COLUMNS.map(([key]) => {
    const v = key === "synced_to_app" ? (l[key] ? "Yes" : "No") : l[key];
    return `"${String(v ?? "").replace(/"/g, '""')}"`;
  }).join(","));
  return [CSV_COLUMNS.map(([, label]) => label).join(","), ...rows].join("\n");
};

const SYNC_FILTERS = [
  ["all", "All"],
  ["pending", "Pending"],
  ["synced", "Synced"],
];

export default function AdminLeads() {
  const { admin, checking, login, logout } = useAdminSession();
  const [leads, setLeads] = useState(null);
  const [query, setQuery] = useState("");
  const [syncFilter, setSyncFilter] = useState("all");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);

  const retrySync = async (leadId) => {
    setRetryingId(leadId);
    try {
      const { data } = await axios.post(`${API}/leads/${leadId}/retry-sync`);
      if (data.synced_to_app) {
        setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, synced_to_app: true } : l)));
        toast.success("Synced to app successfully!");
      } else {
        toast.error("App isn't reachable yet — still pending. Try again later.");
      }
    } catch {
      toast.error("Retry failed. Please try again.");
    } finally {
      setRetryingId(null);
    }
  };

  const retryAllPending = async () => {
    const pending = (leads || []).filter((l) => !l.synced_to_app);
    if (pending.length === 0) return;
    setRetryingAll(true);
    let succeeded = 0;
    for (const l of pending) {
      try {
        const { data } = await axios.post(`${API}/leads/${l.id}/retry-sync`);
        if (data.synced_to_app) {
          succeeded += 1;
          setLeads((prev) => prev.map((p) => (p.id === l.id ? { ...p, synced_to_app: true } : p)));
        }
      } catch (err) {
        console.error("Retry-sync failed for lead", l.id, err);
        // keep going — summarized below
      }
    }
    setRetryingAll(false);
    const failed = pending.length - succeeded;
    if (succeeded > 0 && failed === 0) toast.success(`Synced all ${succeeded} pending lead${succeeded === 1 ? "" : "s"}!`);
    else if (succeeded > 0) toast.message(`Synced ${succeeded}, ${failed} still pending (app not reachable).`);
    else toast.error("App isn't reachable yet — none synced. Try again later.");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(data);
    } catch {
      setLeads(null);
    }
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    let result = leads;
    if (syncFilter === "pending") result = result.filter((l) => !l.synced_to_app);
    if (syncFilter === "synced") result = result.filter((l) => l.synced_to_app);
    const q = query.trim().toLowerCase();
    if (!q) return result;
    return result.filter((l) =>
      [l.name, l.email, l.state, l.location, l.referral_code].some((v) => v?.toLowerCase().includes(q))
    );
  }, [leads, query, syncFilter]);

  const syncSummary = useMemo(() => {
    if (!leads) return { synced: 0, pending: 0 };
    return leads.reduce(
      (acc, l) => (l.synced_to_app ? { ...acc, synced: acc.synced + 1 } : { ...acc, pending: acc.pending + 1 }),
      { synced: 0, pending: 0 }
    );
  }, [leads]);

  const exportCSV = () => {
    const blob = new Blob([toCSV(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relayrigs-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) return null;

  if (!admin) {
    return <AdminLoginForm title="Waitlist Registration List" subtitle="Sign in to view and export the waitlist." onLogin={login} />;
  }

  if (!leads) {
    return (
      <div data-testid="admin-leads-loading" className="min-h-screen bg-rr-surface flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-rr-orange" />
      </div>
    );
  }

  return (
    <div data-testid="admin-leads-dashboard" className="min-h-screen bg-rr-surface py-14 px-5 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-rr-navy">Waitlist Registration List</h1>
            <p className="text-sm text-rr-muted mt-1">{leads.length} total pre-registration{leads.length === 1 ? "" : "s"}</p>
            <div data-testid="admin-leads-sync-summary" className="flex items-center gap-3 mt-2 text-xs font-bold">
              <span className="inline-flex items-center gap-1.5 text-rr-green"><CheckCircle2 size={13} /> {syncSummary.synced} synced</span>
              <span className="inline-flex items-center gap-1.5 text-rr-muted"><Circle size={13} /> {syncSummary.pending} pending</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setChangePasswordOpen(true)}
              data-testid="admin-leads-change-password-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"
            >
              <KeyRound size={14} /> Change Password
            </button>
            <button
              onClick={load}
              data-testid="admin-leads-refresh-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={retryAllPending}
              disabled={retryingAll || syncSummary.pending === 0}
              data-testid="admin-leads-retry-all-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white disabled:opacity-50"
            >
              <RotateCw size={14} className={retryingAll ? "animate-spin" : ""} /> Retry All Pending{syncSummary.pending > 0 ? ` (${syncSummary.pending})` : ""}
            </button>
            <a
              href="/admin/drivers"
              data-testid="admin-leads-drivers-link"
              className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"
            >
              Driver Directory
            </a>
            <button
              onClick={exportCSV}
              data-testid="admin-leads-export-button"
              className="flex items-center gap-2 text-sm font-bold text-white bg-rr-orange rounded-full px-4 py-2 hover:bg-rr-navy transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-leads-logout-button"
              className="flex items-center gap-2 text-sm font-bold text-rr-muted border border-rr-line rounded-full px-4 py-2 hover:border-rr-red hover:text-rr-red transition-colors bg-white"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative max-w-sm flex-1">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-rr-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, state, referral code..."
              data-testid="admin-leads-search-input"
              className="w-full border border-rr-line rounded-full pl-10 pr-4 py-2.5 text-sm bg-white focus:outline-none focus:border-rr-orange"
            />
          </div>
          <div className="flex bg-white border border-rr-line rounded-full p-1">
            {SYNC_FILTERS.map(([value, label]) => (
              <button
                key={value}
                onClick={() => setSyncFilter(value)}
                data-testid={`admin-leads-sync-filter-${value}`}
                className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${
                  syncFilter === value ? "bg-rr-navy text-white" : "text-rr-muted hover:text-rr-navy"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-rr-line overflow-x-auto">
          <table data-testid="admin-leads-table" className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wide text-rr-muted border-b border-rr-line">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Referral Code</th>
                <th className="px-4 py-3">Synced To App</th>
                <th className="px-4 py-3">Signed Up</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`admin-leads-row-${l.id}`} className="border-b border-rr-line last:border-0">
                  <td className="px-4 py-3 font-semibold text-rr-navy">{l.name || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted">{l.phone || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted">{l.role || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted">{[l.location, l.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted font-mono text-xs">{l.referral_code || "—"}</td>
                  <td className="px-4 py-3" data-testid={`admin-leads-synced-${l.id}`}>
                    {l.synced_to_app ? (
                      <span className="inline-flex items-center gap-1.5 text-rr-green font-bold text-xs"><CheckCircle2 size={14} /> Synced</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-rr-muted font-bold text-xs"><Circle size={14} /> Pending</span>
                        <button
                          onClick={() => retrySync(l.id)}
                          disabled={retryingId === l.id}
                          data-testid={`admin-leads-retry-sync-${l.id}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-rr-orange hover:text-rr-navy transition-colors disabled:opacity-50"
                        >
                          <RotateCw size={12} className={retryingId === l.id ? "animate-spin" : ""} /> Retry Sync
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-rr-muted whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-rr-muted">No matching registrations.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AdminChangePasswordModal open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
